import { useSetAtom } from 'jotai';
import useTranslation from 'next-translate/useTranslation';

// Lib
import { aclFeatures } from '@/lib/acl/aclFeatures';
import { MenuEntryType } from '@/lib/platform/menu/menu';

// App routes
import {
    HOME,
    DASHBOARD,
    DASHBOARD_VIEW,
    DASHBOARD_CUSTOMIZATION,
    DASHBOARD_GENERATE_CUSTOM_REPORT,
    ANALYZE_OVERVIEW,
    ANALYZE_ENGAGEMENT,
    ANALYZE_COMMUNITY,
    ANALYZE_REACH_IMPRESSIONS,
    ANALYZE_CONTENT,
    ANALYZE_MENTIONS,
    CONTENT_POSTS,
    SCHEDULER,
    SCHEDULER_COLLABORATION_SETTINGS,
    SCHEDULER_COLLABORATION_NOTIFICATIONS,
    MEDIA_LIBRARY,
    CAPTIONS_LISTS,
    INSPIRATION,
    LISTENING_COMPETITORS,
    LISTENING_COMPETITOR,
    LISTENING_HASHTAGS,
    LISTENING_HASHTAG,
    LISTENING_FEEDS,
    LISTENING_FEED,
    ANALYZE_INDUSTRY_BENCHMARK,
    SOCIAL_PROFILES,
    ACCOUNT,
    PLATFORM_OPTIONS,
    THEMES,
    SECURITY,
    SUBSCRIPTION_INVOICES,
    DATA_ACCESS,
    INTEGRATIONS,
    USERS_ROLES,
    MANAGE_COMMENTS_TRACKER,
    REPORTS,
    NOTIFICATIONS,
    SCHEDULER_FEED_VIEW,
    CONTENT_ALBUMS_COMPARATIVE_TABLE,
    MANAGE_MENTIONS_TRACKER,
    MANAGE_RESOLVED_TRACKER,
    MANAGE_IMPORTANT_TRACKER,
    MANAGE_DM_TRACKER
} from '@/lib/routes/privateRoutes';

// Components
import { menuOverlayAtom } from '@/components/layouts/LayoutPlatform/components/Menu/menuOverlayAtom';
import { ModalUpsell } from '@/components/composite/Modals/ModalUpsell';

export const useMenu = () => {
    const setMenuOverlay = useSetAtom(menuOverlayAtom);
    const { t } = useTranslation('commons');

    /* Menu */

    const mainMenu: MenuEntryType[] = [
        // Home
        {
            id: 'home',
            icon: 'explore',
            link: HOME,
            name: 'commons:menu.home',
            urlActiveMenu: [HOME]
        },
        // Dashboard
        {
            id: 'dashboard',
            icon: 'dashboard',
            link: DASHBOARD,
            name: 'commons:menu.dashboard',
            urlActiveMenu: [
                DASHBOARD_VIEW,
                DASHBOARD_CUSTOMIZATION,
                DASHBOARD_GENERATE_CUSTOM_REPORT
            ]
        },
        // Analytics
        {
            id: 'analytics',
            icon: 'analytics',
            name: 'menu.analytics',
            subMenuItems: [
                {
                    id: 'analyze-overview',
                    link: ANALYZE_OVERVIEW,
                    name: 'commons:menu.overview'
                },
                {
                    id: 'analyze-engagement',
                    link: ANALYZE_ENGAGEMENT,
                    name: 'commons:menu.engagement'
                },
                {
                    id: 'analyze-community',
                    link: ANALYZE_COMMUNITY,
                    name: 'commons:menu.community'
                },
                {
                    id: 'analyze-reach-impressions',
                    link: ANALYZE_REACH_IMPRESSIONS,
                    name: 'commons:menu.reach_impressions'
                },
                {
                    id: 'analyze-content',
                    link: ANALYZE_CONTENT,
                    name: 'commons:menu.content'
                },
                {
                    id: 'analyze-mentions',
                    link: ANALYZE_MENTIONS,
                    name: 'commons:menu.mentions',
                    acl: {
                        permissions_plan_restriction: [aclFeatures.analytic.mentions.read]
                    },
                    onClick: (
                        event: React.MouseEvent,
                        hasAcl: (permissions: string | string[]) => boolean
                    ) => {
                        // Display ModalUpsell if user doesn't have the right permissions
                        if (!hasAcl(aclFeatures.analytic.mentions.read)) {
                            event.preventDefault();
                            setMenuOverlay(
                                <ModalUpsell
                                    heading={t('upsell_modal.mentions.heading')}
                                    description={t('upsell_modal.mentions.description')}
                                    benefits={[
                                        {
                                            text: t('upsell_modal.mentions.benefit1'),
                                            icon: 'check'
                                        },
                                        {
                                            text: t('upsell_modal.mentions.benefit2'),
                                            icon: 'check'
                                        },
                                        {
                                            text: t('upsell_modal.mentions.benefit3'),
                                            icon: 'check'
                                        },
                                        {
                                            text: t('upsell_modal.mentions.benefit4'),
                                            icon: 'plus'
                                        }
                                    ]}
                                    illustrationPath="/images/modals/modalUpsell/mention.png"
                                    dismiss={() => setMenuOverlay('')}
                                />
                            );
                        }
                    }
                }
            ]
        },
        // Reports
        {
            id: 'reports',
            icon: 'reports',
            link: REPORTS,
            name: 'commons:menu.exports_reports'
        },
        // Content
        {
            id: 'content',
            icon: 'content',
            name: 'commons:menu.content',
            subMenuItems: [
                {
                    id: 'content-posts',
                    link: CONTENT_POSTS,
                    name: 'commons:menu.all_posts'
                },
                {
                    id: 'content-albums',
                    link: CONTENT_ALBUMS_COMPARATIVE_TABLE,
                    name: 'commons:menu.campaigns',
                    acl: {
                        permissions_plan_restriction: [aclFeatures.content.campaign.read]
                    },
                    onClick: (
                        event: React.MouseEvent,
                        hasAcl: (permissions: string | string[]) => boolean
                    ) => {
                        // Display ModalUpsell if user doesn't have the right permissions
                        if (!hasAcl(aclFeatures.content.campaign.read)) {
                            event.preventDefault();
                            setMenuOverlay(
                                <ModalUpsell
                                    heading={t('upsell_modal.campaign.heading')}
                                    description={t('upsell_modal.campaign.description')}
                                    benefits={[
                                        {
                                            text: t('upsell_modal.campaign.benefit1'),
                                            icon: 'check'
                                        },
                                        {
                                            text: t('upsell_modal.campaign.benefit2'),
                                            icon: 'check'
                                        },
                                        {
                                            text: t('upsell_modal.campaign.benefit3'),
                                            icon: 'check'
                                        },
                                        {
                                            text: t('upsell_modal.campaign.benefit4'),
                                            icon: 'plus'
                                        }
                                    ]}
                                    illustrationPath="/images/modals/modalUpsell/campaign.png"
                                    dismiss={() => setMenuOverlay('')}
                                />
                            );
                        }
                    }
                }
            ]
        },
        // Publishing
        {
            id: 'publishing',
            icon: 'publishing',
            name: 'commons:menu.publishing',
            subMenuItems: [
                {
                    id: 'publishing-scheduler',
                    link: SCHEDULER,
                    name: 'commons:menu.calendar_view'
                },
                {
                    id: 'publishing-feed-view',
                    link: SCHEDULER_FEED_VIEW,
                    name: 'commons:menu.feed_view'
                },
                {
                    id: 'publishing-collaboration',
                    link: SCHEDULER_COLLABORATION_SETTINGS,
                    name: 'commons:menu.collaboration',
                    urlActiveMenu: [
                        SCHEDULER_COLLABORATION_SETTINGS,
                        SCHEDULER_COLLABORATION_NOTIFICATIONS
                    ],
                    acl: {
                        permissions_plan_restriction: [
                            aclFeatures.publishing.external_collaboration.read
                        ]
                    },
                    onClick: (
                        event: React.MouseEvent,
                        hasAcl: (permissions: string | string[]) => boolean
                    ) => {
                        // Display ModalUpsell if user doesn't have the right permissions
                        if (!hasAcl(aclFeatures.publishing.external_collaboration.read)) {
                            event.preventDefault();
                            setMenuOverlay(
                                <ModalUpsell
                                    heading={t('upsell_modal.collaboration.heading')}
                                    description={t('upsell_modal.collaboration.description')}
                                    benefits={[
                                        {
                                            text: t('upsell_modal.collaboration.benefit1'),
                                            icon: 'check'
                                        },
                                        {
                                            text: t('upsell_modal.collaboration.benefit2'),
                                            icon: 'check'
                                        },
                                        {
                                            text: t('upsell_modal.collaboration.benefit3'),
                                            icon: 'check'
                                        },
                                        {
                                            text: t('upsell_modal.collaboration.benefit4'),
                                            icon: 'plus'
                                        }
                                    ]}
                                    illustrationPath="/images/modals/modalUpsell/collaboration.png"
                                    dismiss={() => setMenuOverlay('')}
                                />
                            );
                        }
                    }
                },
                {
                    id: 'publishing-media-library',
                    link: MEDIA_LIBRARY,
                    name: 'commons:menu.media_library'
                },
                {
                    id: 'publishing-caption-template',
                    link: CAPTIONS_LISTS,
                    name: 'commons:menu.caption_template'
                }
            ]
        },
        // AI Content Inspiration
        {
            id: 'ai-content-inspiration',
            icon: 'inspiration',
            link: INSPIRATION,
            name: 'commons:menu.inspiration'
        },
        // Comments
        {
            id: 'comments',
            icon: 'comments',
            link: MANAGE_COMMENTS_TRACKER,
            name: 'commons:menu.conversations',
            urlActiveMenu: [
                MANAGE_COMMENTS_TRACKER,
                MANAGE_IMPORTANT_TRACKER,
                MANAGE_RESOLVED_TRACKER,
                MANAGE_MENTIONS_TRACKER,
                MANAGE_DM_TRACKER
            ]
        },
        // Listening
        {
            id: 'listening',
            icon: 'listening',
            name: 'commons:menu.listening',
            subMenuItems: [
                {
                    id: 'listening-competitors',
                    link: LISTENING_COMPETITORS,
                    name: 'commons:menu.competitors',
                    urlActiveMenu: [LISTENING_COMPETITOR]
                },
                {
                    id: 'listening-hashtags',
                    link: LISTENING_HASHTAGS,
                    name: 'commons:menu.hashtags',
                    urlActiveMenu: [LISTENING_HASHTAG],
                    acl: {
                        permissions_plan_restriction: [aclFeatures.listening.hashtag.read]
                    },
                    onClick: (
                        event: React.MouseEvent,
                        hasAcl: (permissions: string | string[]) => boolean
                    ) => {
                        // Display ModalUpsell if user doesn't have the right permissions
                        if (!hasAcl(aclFeatures.listening.hashtag.read)) {
                            event.preventDefault();
                            setMenuOverlay(
                                <ModalUpsell
                                    heading={t('upsell_modal.hashtags.heading')}
                                    description={t('upsell_modal.hashtags.description')}
                                    benefits={[
                                        {
                                            text: t('upsell_modal.hashtags.benefit1'),
                                            icon: 'check'
                                        },
                                        {
                                            text: t('upsell_modal.hashtags.benefit2'),
                                            icon: 'check'
                                        },
                                        {
                                            text: t('upsell_modal.hashtags.benefit3'),
                                            icon: 'check'
                                        },
                                        {
                                            text: t('upsell_modal.hashtags.benefit4'),
                                            icon: 'plus'
                                        }
                                    ]}
                                    illustrationPath="/images/modals/modalUpsell/hashtags.png"
                                    dismiss={() => setMenuOverlay('')}
                                />
                            );
                        }
                    }
                },
                {
                    id: 'listening-feeds',
                    link: LISTENING_FEEDS,
                    name: 'commons:menu.feeds',
                    urlActiveMenu: [LISTENING_FEED],
                    acl: {
                        permissions_plan_restriction: [aclFeatures.listening.feed.read]
                    },
                    onClick: (
                        event: React.MouseEvent,
                        hasAcl: (permissions: string | string[]) => boolean
                    ) => {
                        // Display ModalUpsell if user doesn't have the right permissions
                        if (!hasAcl(aclFeatures.listening.feed.read)) {
                            event.preventDefault();
                            setMenuOverlay(
                                <ModalUpsell
                                    heading={t('upsell_modal.feeds.heading')}
                                    description={t('upsell_modal.feeds.description')}
                                    benefits={[
                                        { text: t('upsell_modal.feeds.benefit1'), icon: 'check' },
                                        { text: t('upsell_modal.feeds.benefit2'), icon: 'check' },
                                        { text: t('upsell_modal.feeds.benefit3'), icon: 'check' },
                                        { text: t('upsell_modal.feeds.benefit4'), icon: 'plus' }
                                    ]}
                                    illustrationPath="/images/modals/modalUpsell/feeds.png"
                                    dismiss={() => setMenuOverlay('')}
                                />
                            );
                        }
                    }
                },
                {
                    id: 'listening-industry-benchmark',
                    link: ANALYZE_INDUSTRY_BENCHMARK,
                    name: 'commons:menu.industry_benchmark'
                }
            ]
        }
    ];

    const bottomMenu: MenuEntryType[] = [
        // Notifications
        {
            id: 'notifications',
            icon: 'notification',
            link: NOTIFICATIONS,
            name: 'commons:menu.notifications'
        },
        // Social profiles
        {
            id: 'social-profiles',
            icon: 'single-profile',
            link: SOCIAL_PROFILES,
            name: 'commons:menu.social_profiles'
        },
        // Settings
        {
            id: 'settings',
            icon: 'account',
            link: ACCOUNT,
            name: 'commons:menu.my_account',
            urlActiveMenu: [
                ACCOUNT,
                PLATFORM_OPTIONS,
                THEMES,
                SECURITY,
                SUBSCRIPTION_INVOICES,
                DATA_ACCESS,
                INTEGRATIONS,
                USERS_ROLES
            ],
            displaySubMenuMobile: true
        }
    ];

    return {
        mainMenu,
        bottomMenu
    };
};
